import type { IFilterManagerModel } from "app/pages/sales-intelligence/modules/advanced-search-filtering/services/filters-manager/types";
import type { SearchFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/searchFilterInstance";
import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

/**
 * Saved-search policy: a dirty filter counts toward the savable "change set" unless the filter declares it
 * does not define the search (`affectsSearchDefinition: false`, e.g. the suppression list). The per-filter
 * fact lives on the filter instance; this is where saved-search decides what that fact means for saving.
 */
export const countsTowardChangeSet = <T extends IPlainObject, F extends SearchFilterInstance<T>>(
    filtersManager: IFilterManagerModel<T>,
    key: keyof T,
): boolean => {
    if (!filtersManager.isFilterDirty(key)) {
        return false;
    }

    const config = filtersManager.getFilterConfig(key) as F | undefined;

    return config?.affectsSearchDefinition !== false;
};

/**
 * Dirty filter keys that count toward the savable change set, read from the filters manager as the single
 * source of truth (it owns the grouped/instance-key space). Suppression-list-style filters
 * (`affectsSearchDefinition: false`) are excluded by {@link countsTowardChangeSet}.
 */
export const getDirtyFilterKeys = <T extends IPlainObject>(filtersManager: IFilterManagerModel<T>): Array<keyof T> =>
    filtersManager.getDirtyFilters().filter((key) => countsTowardChangeSet(filtersManager, key));
